import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { SERVICE_TYPES, PRIORITY_OPTIONS } from "../../lib/constants";
import { phonePlaceholder } from "../../lib/company";
import AddressAutocomplete from "../AddressAutocomplete";
import { PinDropInput } from "../PinDropInput";

// Sentinel value used in the "Assigned Truck" Select to represent "no truck"
// (Radix Select doesn't allow empty-string item values).
export const UNASSIGNED = "__unassigned__";

const FORM_FIELDS = [
  "customer_name", "customer_phone", "address", "drop_off_address",
  "vehicle", "rego", "service_type", "priority", "notes",
  // Jun 2026 — `claim_number` had an input bound to `form.claim_number`
  // but was missing from this list, so it was never seeded from the job
  // (the field looked empty even when the job HAD a claim number) and
  // never made it into the payload. Typing a claim number was a no-op.
  "claim_number",
];

/** Project a Job document into the editable form state, normalizing any
 *  placeholder values (e.g. AA "Awaiting details") into blanks. */
export function buildForm(job) {
  const f = {};
  FORM_FIELDS.forEach((k) => { f[k] = job?.[k] ?? ""; });
  // Preserve existing coords so they aren't lost if user doesn't change the address
  f.lat = job?.lat ?? null;
  f.lng = job?.lng ?? null;
  f.drop_off_lat = job?.drop_off_lat ?? null;
  f.drop_off_lng = job?.drop_off_lng ?? null;
  // Truck assignment — empty string in form means "unassigned"; a non-empty truck id
  // means the job is/was assigned to that truck. Editing this in History is the main
  // path for fixing invoicing-time mistakes.
  f.assigned_truck_id = job?.assigned_truck_id || "";
  // Normalize placeholder "Awaiting details" -> blank for nicer editing UX
  if (f.address === "Awaiting details") { f.address = ""; f.lat = null; f.lng = null; }
  if (f.vehicle === "Awaiting details") f.vehicle = "";
  if (job?.customer_name && job.customer_name.startsWith("AA —")) f.customer_name = "";
  return f;
}

// ─────────────────────────────────────────────────────────────────────────────
// Editable form body — name, phone, priority, addresses, vehicle, rego,
// service, truck assignment, notes. All state lives in the parent.
// ─────────────────────────────────────────────────────────────────────────────
const LIVE_STATUSES = ["pending", "booked", "assigned", "accepted", "en_route", "on_site", "awaiting_partner_close"];

export function EditJobFormFields({ form, setForm, trucks, jobStatus, archived }) {
  // Assigning from here skipped every side-effect of a real assignment, so a
  // live job could end up in the queue AND on a truck at once (job 63ccf945,
  // Isaac's Construction). The field stays open on finished and archived jobs,
  // which is the invoicing backfill it was built for.
  const truckLocked = !archived && LIVE_STATUSES.includes(jobStatus);
  const upd = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  return (
    <div className="grid grid-cols-2 gap-3">
      <div className="col-span-2">
        <Label className="uppercase-label">Customer Name</Label>
        <Input
          value={form.customer_name || ""}
          onChange={(e) => upd("customer_name", e.target.value)}
          placeholder="John Smith"
          data-testid="edit-job-input-customer-name"
        />
      </div>

      <div>
        <Label className="uppercase-label">Phone</Label>
        <Input
          value={form.customer_phone || ""}
          onChange={(e) => upd("customer_phone", e.target.value)}
          placeholder={phonePlaceholder()}
          data-testid="edit-job-input-customer-phone"
        />
      </div>

      <div>
        <Label className="uppercase-label">Priority</Label>
        <Select value={form.priority || "normal"} onValueChange={(v) => upd("priority", v)}>
          <SelectTrigger data-testid="edit-job-select-priority">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {PRIORITY_OPTIONS.map((o) => (
              <SelectItem key={o.value} value={o.value}>
                {o.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="col-span-2">
        <Label className="uppercase-label">Pickup Address</Label>
        <AddressAutocomplete
          value={form.address || ""}
          onChange={(v) =>
            setForm((f) => ({ ...f, address: v, lat: null, lng: null }))
          }
          onPick={({ label, lat, lng }) =>
            setForm((f) => ({ ...f, address: label, lat, lng }))
          }
          placeholder="Street address"
          testIdPrefix="edit-job-input-address"
        />
        <p className="text-[11px] text-slate-400 mt-1">Pick a suggestion for instant map pin (no extra geocoding needed).</p>
        <PinDropInput
          testIdPrefix="edit-job-pin-drop"
          onResolved={({ address, lat, lng }) =>
            setForm((f) => ({ ...f, address, lat, lng }))
          }
        />
      </div>

      <div className="col-span-2">
        <Label className="uppercase-label">Drop-off Address</Label>
        <AddressAutocomplete
          value={form.drop_off_address || ""}
          onChange={(v) =>
            setForm((f) => ({ ...f, drop_off_address: v, drop_off_lat: null, drop_off_lng: null }))
          }
          onPick={({ label, lat, lng }) =>
            setForm((f) => ({ ...f, drop_off_address: label, drop_off_lat: lat, drop_off_lng: lng }))
          }
          placeholder="Repair shop / depot address"
          testIdPrefix="edit-job-input-dropoff"
        />
        <p className="text-[11px] text-slate-400 mt-1">Optional — shown as a second pin + route line on the map.</p>
      </div>

      <div>
        <Label className="uppercase-label">Vehicle</Label>
        <Input
          value={form.vehicle || ""}
          onChange={(e) => upd("vehicle", e.target.value)}
          placeholder="2020 Honda Accord"
          data-testid="edit-job-input-vehicle"
        />
      </div>

      <div>
        <Label className="uppercase-label">Rego / Plate</Label>
        <Input
          value={form.rego || ""}
          onChange={(e) => upd("rego", e.target.value.toUpperCase())}
          placeholder="ABC123"
          className="font-mono uppercase"
          data-testid="edit-job-input-rego"
        />
      </div>

      <div className="col-span-2">
        <Label className="uppercase-label">Insurance Claim # <span className="text-slate-400 normal-case font-normal">(optional — separate from any AA / FirstRescue WO)</span></Label>
        <Input
          value={form.claim_number || ""}
          onChange={(e) => upd("claim_number", e.target.value)}
          placeholder="e.g. CL-123456"
          className="font-mono"
          data-testid="edit-job-input-claim-number"
        />
      </div>

      <div className="col-span-2">
        <Label className="uppercase-label">Service Type</Label>
        <Select value={form.service_type || "tow"} onValueChange={(v) => upd("service_type", v)}>
          <SelectTrigger data-testid="edit-job-select-service">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {SERVICE_TYPES.map((s) => (
              <SelectItem key={s.value} value={s.value}>
                {s.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="col-span-2">
        <Label className="uppercase-label">Assigned Truck</Label>
        <Select
          value={form.assigned_truck_id || UNASSIGNED}
          onValueChange={(v) => upd("assigned_truck_id", v === UNASSIGNED ? "" : v)}
          disabled={truckLocked}
        >
          <SelectTrigger data-testid="edit-job-select-truck">
            <SelectValue placeholder="Unassigned" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={UNASSIGNED} data-testid="edit-job-select-truck-unassigned">
              — Unassigned —
            </SelectItem>
            {trucks.map((t) => (
              <SelectItem key={t.id} value={t.id} data-testid={`edit-job-select-truck-${t.id}`}>
                {t.name}{t.driver ? ` · ${t.driver}` : ""}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <p className="text-[11px] text-slate-400 mt-1" data-testid="edit-job-truck-hint">
          {truckLocked
            ? "Locked while the job is live — use Assign to put it on a truck, or Return to Queue to take it off."
            : "Fix or backfill the truck for invoicing. Doesn't change live queue state."}
        </p>
      </div>

      <div className="col-span-2">
        <Label className="uppercase-label">Notes</Label>
        <Textarea
          value={form.notes || ""}
          onChange={(e) => upd("notes", e.target.value)}
          rows={3}
          data-testid="edit-job-input-notes"
        />
      </div>
    </div>
  );
}

/** Build the JSON payload to PUT /api/jobs/{id} — CHANGED FIELDS ONLY.
 *
 *  `baseline` is the form snapshot taken when the sheet opened (i.e. the
 *  server's values at that moment). Every field whose current value still
 *  equals the baseline is omitted.
 *
 *  Jun 2026 — this used to post all nine fields on every save. Combined
 *  with a form that didn't re-seed when the same job was reopened, one
 *  save could silently rewrite fields back to a stale snapshot, stomping
 *  a poller update or a colleague's edit — and `manual_overrides` then
 *  pinned the stale value against future AA/FR polls. Sending only what
 *  the dispatcher actually touched makes a save non-destructive.
 *
 *  Feb 2026 — only touches `assigned_truck_id` when the dispatcher
 *  ACTUALLY changed the dropdown from its initial value, so the backend
 *  can't read an untouched field as "unassign".
 *
 *  Coords travel with their address: they're sent when the address text
 *  changed (or a fresh suggestion was picked) AND we have real coords.
 *  When the address was free-typed without picking a suggestion the
 *  coords are null and we omit them, which is the backend's cue to
 *  geocode server-side.
 */
export function buildUpdatePayload(form, baseline) {
  const base = baseline || {};
  const payload = {};
  FORM_FIELDS.forEach((k) => {
    const next = form[k] ?? "";
    const prev = base[k] ?? "";
    if (next !== prev) payload[k] = next;
  });

  const formTruck = form.assigned_truck_id || "";
  const baseTruck = base.assigned_truck_id || "";
  if (formTruck !== baseTruck) {
    if (formTruck === "") {
      // Dispatcher cleared the dropdown — explicit unassign flag so
      // the backend can distinguish this from "field wasn't touched".
      payload.unassign_truck = true;
    } else {
      // Switched to a real truck (or attached one for the first time).
      payload.assigned_truck_id = formTruck;
    }
  }

  const pickupMoved = payload.address !== undefined
    || form.lat !== (base.lat ?? null)
    || form.lng !== (base.lng ?? null);
  if (pickupMoved && form.lat != null && form.lng != null) {
    payload.lat = form.lat;
    payload.lng = form.lng;
  }
  const dropMoved = payload.drop_off_address !== undefined
    || form.drop_off_lat !== (base.drop_off_lat ?? null)
    || form.drop_off_lng !== (base.drop_off_lng ?? null);
  if (dropMoved && form.drop_off_lat != null && form.drop_off_lng != null) {
    payload.drop_off_lat = form.drop_off_lat;
    payload.drop_off_lng = form.drop_off_lng;
  }
  return payload;
}
